import React from 'react';
import type { DiaryItem } from '../models/diary';

interface DiaryItemCardProps {
  item: DiaryItem;
}

// Function to get fitness icon based on subtype
const getFitnessIcon = (subtype: string): string => {
  switch (subtype.toLowerCase()) {
    case 'cardio':
      return '❤️';
    case 'gym':
      return '🏋️‍♂️';
    case 'sport':
      return '⚽';
    case 'swimming':
      return '🏊‍♂️';
    case 'yoga':
      return '🧘‍♀️';
    case 'pilates':
      return '🤸‍♀️';
    case 'walking':
      return '🚶‍♂️';
    default:
      return '🏃‍♂️'; // Default running icon for unknown subtypes
  }
};

const DiaryItemCard: React.FC<DiaryItemCardProps> = ({ item }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, background: '#e0f7fa', padding: '14px 18px', borderRadius: 10, fontWeight: 500, color: '#222', fontSize: 16, boxShadow: '0 1px 4px rgba(72,187,120,0.04)' }}>
    {/* Icon */}
    <div style={{ fontSize: 28, marginTop: 2 }}>
      {item.type === 'MealItem' && '🍽️'}
      {item.type === 'FitnessItem' && getFitnessIcon(item.subtype)}
      {item.type === 'SupplementItem' && '💊'}
      {item.type === 'MedicationItem' && '💉'}
    </div>
    {/* Info */}
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 2 }}>{item.name}</div>
      <div style={{ fontSize: 14, color: '#38b2ac', marginBottom: 2 }}>🕐 {item.timeOfDay}</div>
      {(() => {
        if (!item.cancellationDate) return null;
        // Format is DD-MM-YYYY from the API
        const parts = item.cancellationDate.split('-');
        if (parts.length !== 3) return null;
        const d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`);
        if (isNaN(d.getTime())) return null;
        return (
          <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 4, backgroundColor: '#fee2e2', padding: '2px 8px', borderRadius: 4 }}>
            <span>⏳</span>
            <span>Vence: {d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
        );
      })()}
      {item.type === 'MealItem' && (
        <>
          <div style={{ fontSize: 14, color: '#555' }}>Subtipo: {item.subtype}</div>
          
          {/* Meal Target Section */}
          {item.mealTarget && (
            <div style={{ 
              marginTop: 8, 
              padding: 10, 
              backgroundColor: '#fef3c7', 
              borderRadius: 6, 
              border: '2px solid #fbbf24' 
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>
                🎯 Objetivos Macro
              </div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: 6, 
                fontSize: 12 
              }}>
                <div style={{ color: '#92400e' }}>
                  <strong>Cal:</strong> {item.mealTarget.calories}
                </div>
                <div style={{ color: '#92400e' }}>
                  <strong>Prot:</strong> {item.mealTarget.protein}g
                </div>
                <div style={{ color: '#92400e' }}>
                  <strong>Carb:</strong> {item.mealTarget.carbs}g
                </div>
                <div style={{ color: '#92400e' }}>
                  <strong>Gras:</strong> {item.mealTarget.fat}g
                </div>
                <div style={{ color: '#92400e' }}>
                  <strong>Fibr:</strong> {item.mealTarget.fiber}g
                </div>
                <div style={{ color: '#92400e' }}>
                  <strong>Agua:</strong> {item.mealTarget.water}ml
                </div>
              </div>
            </div>
          )}
          
          {/* Display meals as recommendations */}
          {item.meals.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 4 }}>
                💡 Recomendaciones:
              </div>
              {item.meals.map((meal, index) => (
                <div key={meal.id || index} style={{ 
                  marginBottom: 6, 
                  padding: 6, 
                  backgroundColor: '#f0f9ff', 
                  borderRadius: 4, 
                  border: '1px solid #e0f2fe' 
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0369a1', marginBottom: 2 }}>
                    {meal.name} - {meal.calories} cal
                  </div>
                  <div style={{ fontSize: 11, color: '#555' }}>
                    {meal.ingredients.map((ingredient, idx) => (
                      <span key={idx}>
                        {ingredient.name} ({ingredient.quantity}g)
                        {idx < meal.ingredients.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {item.type === 'FitnessItem' && (
        <>
          <div style={{ fontSize: 14, color: '#555' }}>Subtipo: {item.subtype}</div>
          
          {/* Display all activities */}
          <div style={{ marginTop: 8 }}>
            {item.activities.map((activity, index) => (
              <div key={activity.id || index} style={{ marginBottom: 8, padding: 8, backgroundColor: '#f0fdf4', borderRadius: 6, border: '1px solid #dcfce7' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#15803d', marginBottom: 4 }}>
                  {activity.name}
                </div>
                <div style={{ fontSize: 13, color: '#555', marginBottom: 2 }}>
                  <strong>Tipo:</strong> {typeof activity.activityType === 'object' ? activity.activityType.name : activity.activityType}
                </div>
                <div style={{ fontSize: 13, color: '#555', marginBottom: 2 }}>
                  <strong>Nivel:</strong> {activity.activityLevel}
                </div>
                <div style={{ fontSize: 13, color: '#555', marginBottom: 2 }}>
                  <strong>Duración:</strong> {activity.expectedDurationMin} min
                </div>
                <div style={{ fontSize: 13, color: '#666' }}>
                  <strong>Detalles:</strong> {activity.details}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {item.type === 'SupplementItem' && (
        <>
          <div style={{ fontSize: 14, color: '#555' }}>Subtipo: {item.subtype}</div>

          {/* Display all supplements */}
          <div style={{ marginTop: 8 }}>
            {item.supplements.map((supplement, index) => (
              <div key={supplement.id || index} style={{ marginBottom: 8, padding: 8, backgroundColor: '#fdf4ff', borderRadius: 6, border: '1px solid #f3e8ff' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#7c3aed', marginBottom: 4 }}>
                  {supplement.supplementCatalog?.name || 'Suplemento'}
                </div>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>
                  <strong>Descripción:</strong> {supplement.description}
                </div>
                {supplement.supplementCatalog?.purchaseUrl && (
                  <a href={supplement.supplementCatalog.purchaseUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#3182ce', textDecoration: 'underline', fontWeight: 500 }}>
                    🛒 Comprar
                  </a>
                )}
              </div>
            ))}
          </div>
        </>
      )}
      {item.type === 'MedicationItem' && (
        <>
          <div style={{ fontSize: 14, color: '#555' }}>Subtipo: {item.subtype}</div>

          {/* Display all medications */}
          <div style={{ marginTop: 8 }}>
            {(item.medications || []).map((medication, index) => (
              <div key={medication.id || index} style={{ marginBottom: 8, padding: 8, backgroundColor: '#fff7ed', borderRadius: 6, border: '1px solid #fed7aa' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#c2410c', marginBottom: 4 }}>
                  {medication.medicationCatalog?.name || 'Medicamento'}
                </div>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>
                  <strong>Instrucciones:</strong> {medication.description}
                </div>
                {medication.medicationCatalog?.purchaseUrl && (
                  <a href={medication.medicationCatalog.purchaseUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#f97316', textDecoration: 'underline', fontWeight: 500 }}>
                    🔗 Ver enlace
                  </a>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  </div>
);

export default DiaryItemCard; 